import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import type { Course } from 'Zustand/courseCompare';

import NextImage from 'next/image';
import { useTheme } from 'styled-components';

import Pill from 'Elements/Pill';
import Feature from '../Feature';
import Tooltip from 'Components/Tooltip';

import {
    PersonChartGrowth,
    Clipboard,
    GraduationCapBook,
    ThumbsUpPage,
    Briefcase,
    PuzzlePieceHand,
    InformationLetter,
    VirtualRealityHeadset
} from '@solent-university/solent-icons';

import {
    Container,
    HeadingsColumn,
    DataColumns,
    Column,
    Heading,
    HeadingText,
    IconWrapper,
    Icon,
    CourseName,
    RemoveButton,
    RemoveIcon,
    CourseLevel,
    CourseImageWrapper,
    Cell,
    Button,
    RemoveTextButton,
    ColumnSlot,
    ColumnSlotContainer,
    RemoveAllTextButton,
    TooltipWrapper,
    TooltipIcon
} from './Table.styled';

export interface Props {
    courses: Course[];
    close: () => void;
    clearCourses: () => void;
    removeCourse: (course: Course) => void;
}

const Table: React.FC<Props> = ({ courses, removeCourse, clearCourses, close }) => {
    const [isScrolling, setIsScrolling] = useState(false);
    const tableRef = useRef() as React.MutableRefObject<HTMLDivElement>;
    const dataColumnsRef = useRef() as React.MutableRefObject<HTMLDivElement>;
    const router = useRouter();
    const { colors } = useTheme();

    const handleDataColumnsScroll = () => {
        setIsScrolling(dataColumnsRef.current.scrollLeft !== 0);
    };

    useEffect(() => {
        const dataColumnsEl = dataColumnsRef.current;
        dataColumnsEl.addEventListener('scroll', handleDataColumnsScroll);

        return () => {
            dataColumnsEl.removeEventListener('scroll', handleDataColumnsScroll);
        };
    }, []);

    return (
        <Container role="table" ref={tableRef}>
            <RemoveButton onClick={close} aria-label="Close course compare">
                <RemoveIcon />
            </RemoveButton>
            <div>
                <HeadingsColumn role="row" $isScrolling={isScrolling}>
                    <Heading role="columnheader" $hidden>
                        Course name
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={PersonChartGrowth} />
                        </IconWrapper>
                        <HeadingText>Course level</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Clipboard} />
                        </IconWrapper>
                        <HeadingText>Entry requirements</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={PuzzlePieceHand} />
                        </IconWrapper>
                        <HeadingText>Foundation year available</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={Briefcase} />
                        </IconWrapper>
                        <HeadingText>Placement year available</HeadingText>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={GraduationCapBook} />
                        </IconWrapper>
                        <HeadingText>Professional accreditation</HeadingText>
                        <TooltipWrapper>
                            <Tooltip
                                id="p-a-tooltip"
                                ariaLabel="Professional accreditation tooltip"
                                tooltip="This means the course meets the requirements of the accrediting professional body. It could also mean exemptions from modules when studying for the professional qualification after graduating."
                                children={<TooltipIcon icon={InformationLetter} />}
                            />
                        </TooltipWrapper>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={ThumbsUpPage} />
                        </IconWrapper>
                        <HeadingText>Built-in work experience placement</HeadingText>
                        <TooltipWrapper>
                            <Tooltip
                                id="w-e-tooltip"
                                ariaLabel="Work experience placement tooltip"
                                tooltip="During this course, students are expected to complete a work experience placement – normally a module in year 2 or 3 – where they can gain vital practical skills required by employers."
                                children={<TooltipIcon icon={InformationLetter} />}
                            />
                        </TooltipWrapper>
                    </Heading>
                    <Heading role="columnheader">
                        <IconWrapper>
                            <Icon as={VirtualRealityHeadset} />
                        </IconWrapper>
                        <HeadingText>Specialist facilities</HeadingText>
                    </Heading>
                </HeadingsColumn>
                <RemoveAllTextButton
                    onClick={() => {
                        clearCourses();
                        close();
                    }}
                    aria-label="Remove all courses from compare"
                    className="m-course-compare-remove-all"
                    $isScrolling={isScrolling}
                >
                    Remove all courses
                </RemoveAllTextButton>
            </div>
            <DataColumns ref={dataColumnsRef} role="rowgroup" $isScrolling={isScrolling}>
                {[...Array(3)].map((_, index) => {
                    const course: Course | undefined =
                        index < courses.length ? courses[index] : undefined;
                    const key: string = course !== undefined ? course.id : `course-slot-${index}`;
                    return (
                        <div key={key}>
                            {courses.length >= 2 && course ? (
                                <>
                                    <Column role="row">
                                        <CourseImageWrapper>
                                            <NextImage
                                                src={course.image.src}
                                                alt=""
                                                width={250}
                                                height={160}
                                            />
                                        </CourseImageWrapper>
                                        <CourseName role="cell">
                                            {course.degreeType && <span>{course.degreeType}</span>}
                                            {course.name}
                                        </CourseName>
                                        <CourseLevel role="cell">
                                            <Pill
                                                backgroundColor={
                                                    course.level === 'Undergraduate'
                                                        ? undefined
                                                        : '#eee8e9'
                                                }
                                                textColor={
                                                    course.level === 'Undergraduate'
                                                        ? undefined
                                                        : colors.accents[9].hex
                                                }
                                            >
                                                {course.level}
                                            </Pill>
                                        </CourseLevel>
                                        <Cell role="cell" style={{ fontWeight: 'bold' }}>
                                            {course.entryRequirements}
                                        </Cell>
                                        <Cell role="cell">
                                            <Feature
                                                hasFeature={course.features.foundationYear}
                                                name="foundation year"
                                            />
                                        </Cell>
                                        <Cell role="cell">
                                            <Feature
                                                hasFeature={course.features.placementYear}
                                                name="placement year"
                                            />
                                        </Cell>
                                        <Cell role="cell">
                                            <Feature
                                                hasFeature={course.features.workExperiencePlacement}
                                                name="built in work experience placement"
                                            />
                                        </Cell>
                                        <Cell role="cell">
                                            <Feature
                                                hasFeature={
                                                    course.features.professionalAccreditation
                                                }
                                                name="built in work professional accreditation"
                                            />
                                        </Cell>
                                        <Cell role="cell">
                                            <Feature
                                                hasFeature={course.features.specialistFacilities}
                                                name="specialist facilities"
                                            />
                                        </Cell>
                                    </Column>
                                    <Button
                                        aria-label={`Go to ${course.name} course page`}
                                        href={course.link.href}
                                        onClick={close}
                                        variant="accentFive"
                                        openInNewTab={true}
                                    >
                                        View course
                                    </Button>
                                    <RemoveTextButton
                                        onClick={() => {
                                            removeCourse(course);
                                            courses.length === 1 && close();
                                        }}
                                        className="m-course-compare-remove"
                                        aria-label={`Remove ${course.name} from compare`}
                                    >
                                        Remove course
                                    </RemoveTextButton>
                                </>
                            ) : (
                                <ColumnSlot isEmpty={!course}>
                                    <ColumnSlotContainer>
                                        <p>Add another course to compare</p>
                                        {router.pathname === '/courses' ? (
                                            <Button
                                                aria-label="Add course to compare"
                                                className="m-course-compare-add"
                                                variant="accentFive"
                                                onClick={close}
                                            >
                                                Add course
                                            </Button>
                                        ) : (
                                            <Button
                                                aria-label="Go to courses page"
                                                className="m-course-compare-add"
                                                variant="accentFive"
                                                href="/courses"
                                            >
                                                Go to courses
                                            </Button>
                                        )}
                                    </ColumnSlotContainer>
                                </ColumnSlot>
                            )}
                        </div>
                    );
                })}
            </DataColumns>
        </Container>
    );
};

export default Table;
